import { BASE_URL, IMeeting, PageRoutes, axiosDownloadPdf, useStore, useViewModel } from '@utils';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PushStore } from '../../store/pushStore';
import { CalendarViewModel, LoginViewModel, ProfileViewModel } from '../../views';
import { MeetingViewModel } from '../calendar';
import { FilterViewModel } from '../filter';
import { HeaderFilter, FilteredMeetingsList } from '../headerFilter';
import { Input } from '../input';
import { ProfileModal } from '../profileModal/ProfileModal';
import { PushModal, PushOpener } from '../pushNotifications';
import { Search, Logo } from '../svgs';
import '../../styles/components/header.scss';
import { toast } from 'react-toastify';


/**
 *  @HIDE  все скрыто
 *  @FILTER открыта выпадашка настроек фильтров
 *  @FILTERED_MEETINGS скрыта вападашка настройки фильтров, открыт список отфильтрованных всртреч
 *  @PROFILE открыта выпадашка профиля
 *  */
type THeaderModal = 'HIDE' | 'FILTER' | 'FILTERED_MEETINGS' | 'PROFILE';

export const HeaderDesktop: FC = observer(() => {
  const [open, setOpen] = useState<THeaderModal>('HIDE');
  const [pushVisibility, setPushVisibility] = useState(false);

  const ref = useRef<HTMLDivElement>();
  const pushRef = useRef<HTMLDivElement>();
  const searchRef = useRef<HTMLDivElement>();

  const filterViewModel = useViewModel(FilterViewModel);
  const profileViewModel = useViewModel(ProfileViewModel);
  const meetingViewModel = useViewModel(MeetingViewModel);
  const calendarViewModel = useViewModel(CalendarViewModel);

  const pushStore = useStore(PushStore);
  const auth = useViewModel(LoginViewModel);

  const navigate = useNavigate();

  const location = useLocation();
  useEffect(() => {
    if (filterViewModel.isAnyFilterHas)
      setOpen((location.pathname === PageRoutes.CALENDAR_PAGE && !meetingViewModel.meetingData) ? 'FILTERED_MEETINGS' : 'HIDE');
  }, [location, meetingViewModel.meetingData]);

  const onClose = useCallback(() => setOpen('FILTERED_MEETINGS'), []);
  const onClosePushModal = useCallback(() => {
    setPushVisibility(false);
    pushStore.readPush();
  }, []);
  const openProfileModal = useCallback(() => setOpen('PROFILE'), []);
  const onClickPushOpener = useCallback(() => {
    setPushVisibility((prev) => !prev);
  }, [pushVisibility]);

  const openFilter = useCallback(() => setOpen((prev) => {
    if (prev === 'FILTER')
      return 'FILTERED_MEETINGS';
    return 'FILTER';
  }), [open]);

  const resetData = useCallback(() => {
    filterViewModel.resetEmployeeAndRoom();
    filterViewModel.resetFilters();
    meetingViewModel.resetMeetingData();
    calendarViewModel.clearCalendarData();
    filterViewModel.setMonthCalendar();
  }, []);

  const onClickMeeting = useCallback((meeting: IMeeting) => {
    meetingViewModel.setIsOpenedFromFilterPage(false);
    meetingViewModel.initMeeting(meeting);
    meetingViewModel.setEditable(false);
  }, []);

  const onClickShowMore = () => {
    setOpen('HIDE');
    navigate(PageRoutes.FILTERED_MEETINGS_PAGE);
  };

  const setSearch = (value: string) => {
    setOpen('FILTERED_MEETINGS');
    filterViewModel.onUpdateFilters('search', value);
  };

  const logout = () => {
    pushStore.disconnect();
    pushStore.clearPushs();
    auth.logout();
  };

  const onLoad = () => {
    const fileURL = `${BASE_URL}/help/pdf`;
    axiosDownloadPdf.get(fileURL)
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Инструкция.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Невозможно загрузить файл'));
  }

  return (
    <nav className="main-header">
      <div className='search-logo-area'>
        <Link to={PageRoutes.CALENDAR_PAGE} onClick={resetData}>
          <Logo />
        </Link>

        {location.pathname === PageRoutes.CALENDAR_PAGE && !meetingViewModel.meetingData && (
          <div
            className='search-area'
            ref={searchRef}
          >
            <Input
              variant={'rounded'}
              classes='gray search-input'
              placeholder='Поиск'
              value={filterViewModel.filters.search}
              onChange={setSearch}
              startIcon={<Search />}
              filter
              fastClearable
              onOpen={openFilter}
            />
          </div>
        )}
        <HeaderFilter
          visibility={open === 'FILTER'}
          ref={searchRef}
          onClose={onClose}
        />
        {open === 'FILTERED_MEETINGS' && filterViewModel.isAnyFilterHas && !filterViewModel.isFilterOnCurrentMonth && (
          <FilteredMeetingsList
            meetings={calendarViewModel.filteredMeetingsValues}
            onClickMeeting={onClickMeeting}
            onClickShowMore={onClickShowMore}
            onReset={filterViewModel.resetFilters}
          />
        )}
      </div>

      <div className="settings-bar">
        <div className='clickable help-button' onClick={onLoad}>Помощь</div>
        <div
          ref={pushRef}
          onClick={onClickPushOpener}
          className='push-opener'
        >
          <PushOpener
            isOpen={pushVisibility}
            newMessagesCount={pushStore.newPushsCount}
          />
        </div>
        <PushModal
          onClose={onClosePushModal}
          visible={pushVisibility}
          ref={pushRef}
        />
        <div
          className="image"
          ref={ref}
          onClick={openProfileModal}
        >
          {!profileViewModel.isLoading && (
            <img
              className="header-avatar"
              src={profileViewModel.profileInfo.img}
              alt="profile"
            />
          )}
        </div>
        <ProfileModal
          onClose={onClose}
          onLogout={logout}
          visibility={open === 'PROFILE'}
          fullName={profileViewModel.profileInfo.fullName}
          email={profileViewModel.profileInfo.corporateEmail}
          img={profileViewModel.profileInfo.img}
          ref={ref}
        />
      </div>
    </nav>
  );
});
